import { Module, ActionContext } from 'vuex';
import { rootState } from '../state';
import { Basket } from '@/api/basket';
import { Product, sProductsToProducts } from '@/api/product';
import { Client } from '@/api/profile';
import REQ_URL from '@/consts/req-url';
import { $fetch } from '@/api/fetch';
import cookie from '@/api/cookie';
import { updateProductCountOpts, addProductToBasketOpts } from '@/types/basket';
import { snackbarParams } from '@/types/state.modal';
import storageObservable from '@/api/storageObservable';
import { maxItemCount } from '@/consts/limits';
import gaussRound from '@/api/gaussRound';
import {
    BasketLike, BasketSnapshot, basketSnapshot, sameSnapshot, isBasketLike,
    mergeBasketItems, readDraft, writeDraft, hasDraft, removeDraft, removeExpiredDrafts
} from '@/api/basketSync';

let updateTimerID: null | number = null
let timerInterval = 1500

const state = {
    basket: new Basket(),
    //предупреждение, пока изменения корзины не удаётся сохранить на сервере
    saveError: ''
}

type Context = ActionContext<typeof state, typeof rootState>

///Синхронизация корзины с сервером (состояние своё у каждой вкладки), подробнее в api/basketSync.ts

//через сколько корзина считается давно не сверявшейся с сервером
const STALE_AFTER = 2 * 60 * 1000
const PULL_RETRY_AFTER = 30 * 1000
const PULL_TIMEOUT = 15 * 1000
const SAVE_TIMEOUT = 2 * 60 * 1000
//сколько заказ ждёт сверки корзины с сервером
const VERIFY_TIMEOUT = 20 * 1000
const RETRY_DELAYS = [5000, 15000, 30000, 60000]
const MAX_STALE_ROUNDS = 2

//состав и ID корзины, какими их последний раз видел сервер
let base: BasketSnapshot | null = basketSnapshot(state.basket)
let baseID: number | undefined = state.basket.ID
//счётчик локальных изменений и то его значение, которое уже сохранено на сервере
let revision = 0
let savedRevision = 0
let lastChangeAt = 0
//номера изменений, которыми здесь последний раз меняли комментарий и отметки позиций: пока такое изменение
//не сохранено, при слиянии с серверной корзиной остаётся своё значение, иначе берётся серверное
let noteRevision = 0
let selectedRevision: { [vCode: string]: number } = {}
//черновик восстановлен: что именно в нём меняли, неизвестно, поэтому его комментарий и отметки считаются своими
let restoredRevision = 0
//изменения, уже разосланные остальным вкладкам
let broadcastRevision = 0
let lastSyncAt = 0
let lastPullAt = 0
//растёт при замене корзины целиком: ответы на запросы, начатые до замены, игнорируются
let epoch = 0
let failedAttempts = 0
let staleRounds = 0
let retryTimerID: null | number = null
let draftScheduled = false
//идёт оформление заказа
let ordering = false
//корзину после оформления заказа надо сохранить как есть, без сверки с серверной
let forcePush = false
//черновик восстановлен без связи с сервером: пока корзина не сверена, отправлять её нельзя
let unverified = false
//сервер не знает get_basket (старый API без версий): корзина сверяется через профиль,
//а узнавать версию перед записью бессмысленно
//признак запоминается на час, чтобы не проверять это после каждой загрузки страницы
const NO_ENDPOINT_KEY = 'basketNoEndpoint'
const NO_ENDPOINT_TTL = 60 * 60 * 1000
let noBasketEndpoint = false
try {
    noBasketEndpoint = Date.now() - +(localStorage.getItem(NO_ENDPOINT_KEY) || 0) < NO_ENDPOINT_TTL
} catch (error) {
}
//запросы по корзине выполняются строго по очереди
let queue: Promise<void> = Promise.resolve()

const isDirty = () => revision !== savedRevision
const ownNote = () => Math.max(noteRevision, restoredRevision) > savedRevision
const ownSelection = (vCode: string) => Math.max(selectedRevision[vCode] || 0, restoredRevision) > savedRevision
//версия от сервера - целое число (иногда строкой), всё остальное считается неизвестной версией
const toVersion = (value: any) => value !== null && value !== undefined && value !== '' && Number.isInteger(+value) ? +value : undefined

removeExpiredDrafts()

function enqueue(task: () => Promise<void>) {
    const run = queue.then(task)
    queue = run.catch((error) => console.error(error))
    return run
}

function withTimeout<T>(timeout: number, request: (signal?: AbortSignal) => Promise<T>) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined
    return new Promise<T>((resolve, reject) => {
        const timerID = setTimeout(() => {
            if (controller) controller.abort()
            reject(new Error('сервер не отвечает'))
        }, timeout)
        request(controller ? controller.signal : undefined).then(
            (res) => {
                clearTimeout(timerID)
                resolve(res)
            },
            (error) => {
                clearTimeout(timerID)
                reject(error)
            }
        )
    })
}

function notify(c: Context, type: snackbarParams['type'], msg: string) {
    c.dispatch('snackbar:after:open', <snackbarParams>{ type, msg })
}

function resetSync(basket: Basket) {
    epoch++
    base = basketSnapshot(basket)
    baseID = basket.ID
    savedRevision = revision
    failedAttempts = staleRounds = 0
    forcePush = false
    unverified = false
    selectedRevision = {}
    if (updateTimerID !== null) clearTimeout(updateTimerID)
    if (retryTimerID !== null) clearTimeout(retryTimerID)
    updateTimerID = retryTimerID = null
}

function markChanged(c: Context) {
    revision++
    lastChangeAt = Date.now()
    if (draftScheduled) return
    draftScheduled = true
    //один черновик на пачку изменений (например, загрузка из xls)
    Promise.resolve().then(() => {
        draftScheduled = false
        saveDraft(c)
    })
}

function saveDraft(c: Context) {
    const token = c.state.basket.token
    if (token && isDirty()) writeDraft(token, { basket: c.state.basket, base, baseID, changedAt: lastChangeAt })
}

function scheduleSync(c: Context) {
    if (updateTimerID !== null) clearTimeout(updateTimerID)
    updateTimerID = setTimeout(() => c.dispatch('syncBasket'), timerInterval)
}

//рассылка корзины остальным вкладкам
function broadcast(c: Context) {
    try {
        storageObservable.emit('basket', c.state.basket)
        if (c.getters.profileType === 'manager') c.commit('updateManagerClient', {
            cart: c.state.basket
        })
    } catch (error) {
        //переполнение localStorage не должно мешать работе с корзиной
        console.error(error)
    }
}

function broadcastVersion(c: Context) {
    try {
        storageObservable.emit('basketVersion', { token: c.state.basket.token, version: c.state.basket.version, items: basketSnapshot(c.state.basket) })
    } catch (error) {
        console.error(error)
    }
}

/**
 * Корзина с сервера, undefined — получить не удалось
 */
async function fetchServerBasket(c: Context, signal?: AbortSignal): Promise<BasketLike | 'unchanged' | 'unknown' | undefined> {
    const token = c.state.basket.token
    const type = c.getters.profileType
    if (!noBasketEndpoint && (type === 'client' || type === 'manager')) {
        try {
            const query = []
            if (type === 'manager') query.push('id=' + encodeURIComponent(token))
            //с известной версией сервер отвечает без корзины, если она не менялась; с черновиком корзина нужна целиком
            const version = c.state.basket.version
            if (version !== undefined && !hasDraft(token)) query.push('version=' + version)
            const data = await $fetch<BasketLike | null>(REQ_URL.GET_BASKET + (query.length ? '?' + query.join('&') : ''), { signal })
            if (data.body === null && version !== undefined && +(data as any).version === version) return 'unchanged'
            if (isBasketLike(data.body) && data.body.token === token) return data.body
        } catch (error) {
            if (error && error.code === 404) {
                noBasketEndpoint = true
                try {
                    localStorage.setItem(NO_ENDPOINT_KEY, String(Date.now()))
                } catch (e) {
                }
            }
        }
    }
    let data: { body?: Client }
    if (c.getters.profileType === 'client') {
        const ssid = cookie.getCookie('PHPSESSID')
        if (!ssid) return undefined
        data = await $fetch<Client>(REQ_URL.GET_PROFILE, {
            headers: {
                'PHPSESSID': ssid
            },
            signal
        })
    } else if (c.getters.profileType === 'manager') {
        data = await $fetch<Client>(REQ_URL.GET_CLIENT, {
            method: 'POST',
            body: JSON.stringify({
                id: token
            }),
            signal
        })
    } else return undefined

    const client = data.body
    //cached - профиль из IndexedDB (сервис-воркер, нет сети), а не с сервера
    if (client && (client as any).cached) return undefined
    //сервер ответил, но корзины этого клиента в ответе нет
    if (!client || client.c1code !== token || !('cart' in client)) return 'unknown'
    const cart: any = client.cart
    if (!cart) return { items: [] }
    return isBasketLike(cart) ? cart : 'unknown'
}

//пока у клиента нет сохранённой корзины, сервер на каждый запрос отдаёт новую пустую,
//её ID и метка равны текущему времени в секундах: смена такого ID ничего не значит
const isGenerated = (basket: BasketLike) => !(basket.items || []).length && basket.ID === basket.timestamp && +basket.ID! < 1e11

//на сервере уже не та корзина, поверх которой сделаны локальные изменения: её ID сменился, и сменили его не здесь
const replacedOnServer = (remote: BasketLike, knownID: number | undefined, localID: number | undefined) =>
    remote.ID !== undefined && knownID !== undefined && +remote.ID !== +knownID && +localID! === +knownID && !isGenerated(remote)

const hasOwn = (obj: object, key: string) => Object.prototype.hasOwnProperty.call(obj, key)
const selectedOf = (items: { Артикул: string, selected?: boolean }[]) => {
    const selected: { [vCode: string]: boolean } = {}
    items.forEach((item) => {
        selected[item.Артикул] = item.selected !== false
    })
    return selected
}

//комментарий и отметки позиций совпадают с серверными (состав корзин одинаковый)
function sameDetails(basket: Basket, remote: BasketLike) {
    if ((basket.note || '') !== (remote.note || '')) return false
    const selected = selectedOf(basket.items)
    return (remote.items || []).every((item) => selected[item.Артикул] === (item.selected !== false))
}

//комментарий и отметки позиций берутся с сервера, кроме изменённых здесь и ещё не сохранённых
//own - свои отметки до слияния: слияние могло подставить серверную позицию вместе с её отметкой
function takeRemoteDetails(basket: Basket, remote: BasketLike, own = selectedOf(basket.items)) {
    if (!ownNote()) basket.note = remote.note || ''
    const selected = selectedOf(remote.items || [])
    basket.items.forEach((item) => {
        const source = ownSelection(item.Артикул) ? own : selected
        if (hasOwn(source, item.Артикул)) item.selected = source[item.Артикул]
    })
}

//то, что уйдёт в заказ и что видит пользователь: комментарий, позиции, количество, отметки
const orderView = (basket: Basket) => JSON.stringify([
    basket.note || '',
    basket.items.map((item) => [item.Артикул, +item.quantity, item.selected]).sort()
])

/**
 * Заказ уходит с корзиной этой вкладки, поэтому перед ним она сверяется с сервером: изменения с другого
 * устройства пользователь должен увидеть до заказа, иначе они пропали бы при записи корзины после него
 */
async function syncBeforeOrder(c: Context) {
    //несохранённые изменения сначала отправляются: при отказе сервера они сливаются с его корзиной
    if (isDirty()) await pushBasket(c)
    await pullBasket(c)
}

/**
 * Возврат изменений, не дошедших до сервера: не было сети, ошибка сервера, вкладку закрыли раньше отправки
 */
function restoreDraft(c: Context, remote?: BasketLike) {
    const token = c.state.basket.token
    const draft = !isDirty() && token ? readDraft(token) : null
    if (!draft) return
    //ID корзины меняется при оформлении заказа: на сервере уже следующая корзина, и изменения,
    //сделанные поверх прежней, к ней не относятся (их позиции могли уйти в тот заказ)
    //если ID сменили здесь же (свой заказ, остаток не дошёл до сервера), черновик свежее серверной корзины и остаётся
    if (remote && replacedOnServer(remote, draft.baseID, draft.basket.ID)) return removeDraft(token)
    const changed = !sameSnapshot(basketSnapshot(draft.basket), basketSnapshot(c.state.basket))
    //версия - та, что известна сейчас: черновик помнит более старую
    const version = c.state.basket.version !== undefined ? c.state.basket.version : draft.basket.version
    c.commit('setBasket', { ...draft.basket, token, version })
    base = draft.base
    baseID = draft.baseID
    lastChangeAt = draft.changedAt
    revision++
    restoredRevision = revision
    if (changed) notify(c, 'info', 'Восстановлены изменения корзины, которые не были сохранены на сервере.')
}

/**
 * С сервера пришла актуальная корзина: принимаем её либо сливаем с несохранёнными изменениями
 */
function applyRemote(c: Context, remote: BasketLike) {
    const token = c.state.basket.token
    const remoteSnapshot = basketSnapshot(remote)
    lastSyncAt = Date.now()
    //черновик, восстановленный без связи с сервером, проходит ту же проверку, что и при обычном восстановлении:
    //на сервере уже следующая корзина (после заказа) - его изменения к ней не относятся
    if (unverified && replacedOnServer(remote, baseID, c.state.basket.ID)) {
        c.commit('setBasket', { ...remote, token })
        removeDraft(token)
        notify(c, 'warning', 'Несохранённые изменения корзины отброшены: с другого устройства уже оформлен заказ.')
        broadcast(c)
        return
    }
    restoreDraft(c, remote)

    if (!sameSnapshot(remoteSnapshot, base)) {
        if (isDirty()) {
            //корзину изменили и здесь, и с другого устройства
            const before = basketSnapshot(c.state.basket)
            c.commit('mergeRemoteBasket', { remote, base })
            if (!sameSnapshot(before, basketSnapshot(c.state.basket)))
                notify(c, 'warning', 'Корзину изменили с другого устройства - изменения объединены. Проверьте корзину перед оформлением заказа.')
        } else {
            c.commit('setBasket', { ...remote, token })
            broadcast(c)
        }
        base = remoteSnapshot
    } else if (isDirty()) {
        //состав на сервере прежний, но комментарий и отметки позиций могли изменить с другого устройства
        c.commit('mergeRemoteBasketDetails', remote)
    } else if (!sameDetails(c.state.basket, remote)) {
        c.commit('setBasket', { ...remote, token })
        broadcast(c)
    }
    if (remote.ID !== undefined) baseID = remote.ID
    //следующая запись делается поверх этой серверной корзины
    unverified = false
    if (remote.version !== undefined) c.commit('setBasketVersion', remote.version)
    if (isDirty()) {
        saveDraft(c)
        scheduleSync(c)
    }
}

/**
 * Сверка корзины с сервером. Если она не удалась, черновик показывается как несохранённые изменения,
 * но на сервер не уходит, пока сверка не пройдёт
 */
async function pullBasket(c: Context) {
    if (ordering || forcePush || !c.state.basket.token) return
    lastPullAt = Date.now()
    const pullEpoch = epoch
    let remote: BasketLike | 'unchanged' | 'unknown' | undefined
    try {
        remote = await withTimeout(PULL_TIMEOUT, (signal) => fetchServerBasket(c, signal))
    } catch (error) {
        console.error(error)
    }
    if (pullEpoch !== epoch || ordering || forcePush) return
    if (remote === 'unchanged') {
        lastSyncAt = Date.now()
        unverified = false
        return
    }
    if (remote === 'unknown') {
        //связь есть, сверять не с чем: черновик уходит как есть, иначе он не сохранился бы никогда
        unverified = false
        return
    }
    if (remote) return applyRemote(c, remote)
    //сервер недоступен: изменения из черновика возвращаем как несохранённые - с предупреждением и повторами
    if (isDirty()) return
    restoreDraft(c)
    if (isDirty()) {
        unverified = true
        scheduleSync(c)
    }
}

/**
 * Корзина не сохранена: предупреждение (одно на серию неудач) и повтор с растущей паузой
 */
function saveFailed(c: Context, msg: string) {
    c.commit('setBasketSaveError', msg)
    if (!failedAttempts) notify(c, 'warning', msg + ' Изменения остаются на этом устройстве, отправка повторится автоматически.')
    retryTimerID = setTimeout(() => c.dispatch('syncBasket'), RETRY_DELAYS[Math.min(failedAttempts, RETRY_DELAYS.length - 1)])
    failedAttempts++
    saveDraft(c)
}

/**
 * Отправка несохранённых изменений корзины на сервер
 */
async function pushBasket(c: Context) {
    if (updateTimerID !== null) clearTimeout(updateTimerID)
    if (retryTimerID !== null) clearTimeout(retryTimerID)
    updateTimerID = retryTimerID = null
    if (!isDirty()) return

    if (unverified && !forcePush) {
        //без сверки черновик перезаписал бы серверную корзину целиком, какой бы она ни была
        if (ordering) return scheduleSync(c)
        await pullBasket(c)
        if (!isDirty()) return
        if (unverified) return saveFailed(c, 'Нет связи с сервером: корзина не сохранена.')
    }
    //отправка перезапишет серверную корзину целиком, поэтому давно не сверявшуюся корзину сначала сверяем:
    //её могли изменить с другого устройства
    //без версии сервер перезапишет корзину без проверки, поэтому неизвестную версию сначала узнаём
    const unsynced = Date.now() - lastSyncAt > STALE_AFTER || (c.state.basket.version === undefined && !noBasketEndpoint)
    if (unsynced && Date.now() - lastPullAt > PULL_RETRY_AFTER) await pullBasket(c)
    if (!isDirty()) return

    const pushEpoch = epoch
    const token = c.state.basket.token
    const sentRevision = revision
    const sentID = c.state.basket.ID
    c.commit('updateBasketTimestamp')
    const sentSnapshot = basketSnapshot(c.state.basket)
    let returned: BasketLike | undefined
    let conflict = false
    let savedVersion: any
    let error: any
    try {
        let data: any = await withTimeout(SAVE_TIMEOUT, (signal) => $fetch<BasketLike | undefined>(REQ_URL.PUT_BASKET, {
            method: 'PUT',
            body: JSON.stringify(c.state.basket),
            signal
        }))
        if (isBasketLike(data.body)) returned = data.body
        //сервер не сохранил корзину: она построена на старой версии, в body - актуальная
        conflict = data.conflict === true && !!returned
        savedVersion = data.version
    } catch (err) {
        error = err
    }
    //пока шёл запрос, корзину заменили целиком: другой клиент, выход, другая вкладка
    if (pushEpoch !== epoch) return

    //следующая отправка должна быть новее всего, что есть на сервере
    if (returned) c.commit('raiseBasketTimestamp', returned.timestamp)
    //без признака conflict (старый API) отказ узнаётся по тому, что сервер прислал обратно другую корзину
    if (returned && (conflict || !sameSnapshot(basketSnapshot(returned), sentSnapshot))) {
        //повторная запись, в том числе после заказа, когда содержимое остаётся своим, идёт уже с актуальной версией
        if (returned.version !== undefined) c.commit('setBasketVersion', returned.version)
        if (++staleRounds > MAX_STALE_ROUNDS) {
            //сервер упорно не принимает корзину - остаётся взять серверную
            c.commit('setBasket', { ...returned, token })
            lastSyncAt = Date.now()
            removeDraft(token)
            notify(c, 'warning', 'Корзина обновлена с сервера: её изменили с другого устройства. Проверьте состав корзины.')
        } else if (forcePush || ordering) scheduleSync(c)
        else applyRemote(c, returned)
    } else if (error) {
        console.error('Корзина не сохранена на сервере:', error)
        //901 - ответ сервис-воркера об отсутствии сети
        const msg = error.code === 901
            ? 'Нет соединения с интернетом: корзина не сохранена на сервере.'
            : `Не удалось сохранить корзину на сервере (${error.code ? 'ошибка ' + error.code : error.message}).`
        saveFailed(c, msg)
    } else {
        if (savedVersion !== undefined) c.commit('setBasketVersion', savedVersion)
        base = sentSnapshot
        baseID = sentID
        savedRevision = sentRevision
        lastSyncAt = Date.now()
        staleRounds = 0
        forcePush = false
        if (failedAttempts) notify(c, 'success', 'Корзина сохранена на сервере.')
        failedAttempts = 0
        c.commit('setBasketSaveError', '')
        if (isDirty()) saveDraft(c)
        else removeDraft(token)
    }
    //остальным вкладкам изменения рассылаются один раз: повторы отправки не должны затирать их свежие правки
    if (returned || broadcastRevision !== sentRevision) broadcast(c)
    //повтор принят: состав у других вкладок уже есть, им нужна только новая версия, иначе их первая правка получит отказ
    else if (!error && !isDirty()) broadcastVersion(c)
    broadcastRevision = sentRevision
}

export const basketStore: Module<typeof state, typeof rootState> = {
    state,
    mutations: {
        setBasket(state, {
            token = '',
            note = '',
            /*attributes = {},
            total_discount = 0,
            total_weight = 0,*/
            items = [],
            timestamp = Date.now(),
            ID = Date.now(),
            version = undefined,
        }: Basket) {
            if (state.basket.token !== token) lastSyncAt = 0
            state.basket.token = token
            state.basket.note = note
            /*state.basket.attributes = attributes
            state.basket.total_discount = total_discount
            state.basket.total_weight = total_weight*/
            state.basket.timestamp = timestamp
            state.basket.items = sProductsToProducts(items)
            state.basket.ID = ID
            state.basket.version = toVersion(version)
            //корзину заменили целиком: считаем её совпадающей с серверной, несохранённых изменений нет
            state.saveError = ''
            resetSync(state.basket)
        },
        //слияние несохранённых изменений с корзиной, которую изменили с другого устройства
        mergeRemoteBasket(state, { remote, base }: { remote: BasketLike, base: BasketSnapshot | null }) {
            const own = selectedOf(state.basket.items)
            state.basket.items = mergeBasketItems(state.basket.items, sProductsToProducts(<any>remote.items || []), base)
            state.basket.timestamp = Math.max(+state.basket.timestamp || 0, +remote.timestamp! || 0)
            //ID - от серверной корзины: свой старый вернул бы ей ID уже оформленного заказа
            if (remote.ID !== undefined) state.basket.ID = remote.ID
            if (remote.version !== undefined) state.basket.version = toVersion(remote.version)
            takeRemoteDetails(state.basket, remote, own)
        },
        mergeRemoteBasketDetails(state, remote: BasketLike) {
            takeRemoteDetails(state.basket, remote)
        },
        setBasketVersion(state, version: number) {
            state.basket.version = toVersion(version)
        },
        raiseBasketTimestamp(state, timestamp: number) {
            state.basket.timestamp = Math.max(+state.basket.timestamp || 0, +timestamp || 0)
        },
        setBasketSaveError(state, msg: string) {
            state.saveError = msg
        },
        updateProductCount(state, {
            vCode,
            count
        }: updateProductCountOpts) {
            let product = state.basket.items.find((product) => {
                return product.Артикул === vCode
            })
            if (product) product.quantity = count > maxItemCount ? maxItemCount : count
        },
        addNewProductToBasket(state, product: Product) {
            let newProduct = new Product(product);
            newProduct.quantity = product.quantity
            newProduct.selected = true
            state.basket.items.push(newProduct)
        },
        selectProduct(state, vCode: string) {
            let product = state.basket.items.find(p => p.Артикул === vCode)
            if (product) product.selected = true
        },
        toggleProductSelected(state, vCode: string) {
            let product = state.basket.items.find(p => p.Артикул === vCode)
            if (product) product.selected = !product.selected
        },
        setAllProductsSelected(state, value: boolean) {
            state.basket.items.forEach(p => { p.selected = value })
        },
        deleteProductFromBasket(state, vCode) {
            let ind = state.basket.items.findIndex((productB) => {
                return productB.Артикул === vCode
            })
            if (ind !== -1) state.basket.items.splice(ind, 1)
        },
        //метка всегда новее той, с которой корзина пришла: сервер отклоняет корзины со старой меткой,
        //и отстающие часы устройства не должны мешать сохранению
        updateBasketTimestamp(state) {
            state.basket.timestamp = Math.max(Date.now(), (+state.basket.timestamp || 0) + 1)
        },
        // новый ID после оформления заказа: по ID отличаются заказы,
        // в т.ч. ключ отложенного офлайн-заказа PLACE_AN_ORDER+ID в sw.js
        renewBasketID(state) {
            state.basket.ID = Date.now()
        },
        updateBasketComment(state, comment: string) {
            state.basket.note = comment
            //комментарий сохраняется позже (по change), но корзину уже нельзя подменять серверной
            revision++
            noteRevision = revision
            lastChangeAt = Date.now()
        },
        clearBasketItems(state) {
            state.basket.items = []
        }
    },
    actions: {
        addProductToBasket(c, { product, quiet }: addProductToBasketOpts) {
            if (!quiet)
                c.dispatch('snackbar:after:open', {
                    type: 'info',
                    msg: product.Наименование + ' - x' + product.quantity
                })
            else c.commit('snackbar:close')

            let productFromBasket = c.state.basket.items.find((productB) => {
                return productB.Артикул === product.Артикул
            })
            if (productFromBasket) {
                c.commit('selectProduct', product.Артикул)
                c.dispatch('updateProductCount', <updateProductCountOpts>{
                    vCode: product.Артикул,
                    count: product.quantity + productFromBasket.quantity,
                })
                selectedRevision[product.Артикул] = revision
            } else c.dispatch('addNewProductToBasket', product)
        },
        updateProductCount(c, params: updateProductCountOpts) {
            c.commit('updateProductCount', params)
            c.dispatch('saveBasketWithThrottling')
        },
        addNewProductToBasket(c, product: Product) {
            c.commit('addNewProductToBasket', product)
            c.dispatch('saveBasketWithThrottling')
            //позиция добавлена отмеченной: отметка своя, даже если на сервере этот товар уже лежит без неё
            selectedRevision[product.Артикул] = revision
        },
        deleteProductFromBasket(c, vCode: string) {
            c.commit('deleteProductFromBasket', vCode)
            c.dispatch('saveBasketWithThrottling')
        },
        toggleProductSelected(c, vCode: string) {
            c.commit('toggleProductSelected', vCode)
            c.dispatch('saveBasketWithThrottling')
            selectedRevision[vCode] = revision
        },
        setAllProductsSelected(c, value: boolean) {
            c.commit('setAllProductsSelected', value)
            c.dispatch('saveBasketWithThrottling')
            c.state.basket.items.forEach((item) => {
                selectedRevision[item.Артикул] = revision
            })
        },
        //vCodes - позиции, ушедшие в заказ: пока он оформлялся, корзину и отметки могла заменить другая вкладка
        deleteSelectedProducts(c, vCodes?: string[]) {
            (vCodes || c.state.basket.items.filter(i => i.selected).map(item => item.Артикул))
                .forEach(vCode => c.commit('deleteProductFromBasket', vCode))
            //после оформления заказа верна корзина этой вкладки: сервер мог изменить свою при оформлении
            if (ordering) forcePush = true
            return c.dispatch('saveBasket')
        },
        //сохранить сделанные изменения сразу
        saveBasket(c) {
            markChanged(c)
            return c.dispatch('syncBasket')
        },
        saveBasketWithThrottling(c) {
            markChanged(c)
            scheduleSync(c)
        },
        //отправить несохранённые изменения, если они есть
        syncBasket(c) {
            return enqueue(() => pushBasket(c))
        },
        //сверить корзину с сервером
        refreshBasket(c) {
            return enqueue(() => pullBasket(c))
        },
        //сверка перед заказом, см. syncBeforeOrder. false - корзина уже не та, что видел пользователь,
        //заказ отправлять нельзя. Заказ сверка надолго не задерживает: если сервер не отвечает, он уходит без неё
        verifyBasketBeforeOrder(c) {
            //снимок - в момент нажатия: запросы, уже стоящие в очереди (сверка по возврату на вкладку),
            //могут изменить корзину раньше, чем очередь дойдёт до сверки
            const seen = orderView(c.state.basket)
            const unchanged = () => seen === orderView(c.state.basket)
            const check = enqueue(() => syncBeforeOrder(c)).then(unchanged, unchanged)
            return Promise.race([check, new Promise<boolean>((resolve) => setTimeout(() => resolve(unchanged()), VERIFY_TIMEOUT))])
        },
        //вкладку снова открыли: досохраняем изменения либо подтягиваем корзину, изменённую с другого устройства
        basketOnReturn(c) {
            if (isDirty()) c.dispatch('syncBasket')
            else if (Date.now() - Math.max(lastSyncAt, lastPullAt) > STALE_AFTER) c.dispatch('refreshBasket')
        },
        //другая вкладка сохранила на сервере корзину, которая уже есть в этой: остаётся взять её версию
        setBasketVersionFromTab(c, { token, version, items }: { token: string, version: number, items: BasketSnapshot }) {
            if (isDirty() || token !== c.state.basket.token || !sameSnapshot(items, basketSnapshot(c.state.basket))) return
            c.commit('setBasketVersion', version)
            base = items
        },
        //на время оформления заказа корзина не сверяется с сервером
        setBasketOrdering(c, value: boolean) {
            ordering = value
        },
        productsFromXls() {

        },
        setBasket(c, basket: Basket) {
            basket.token = (c.rootState as any).profile.clientInfo.c1code
            c.commit('setBasket', basket)
            //есть черновик - значит, на сервере не эта корзина, а та, поверх которой он сделан
            const draft = basket.token ? readDraft(basket.token) : null
            if (draft) {
                base = draft.base
                baseID = draft.baseID
            }
        },
        //корзина только что получена с сервера вместе с профилем
        setServerBasket(c, basket: Basket) {
            c.dispatch('setBasket', basket)
            applyRemote(c, basket)
        },
        //корзина из профиля, сохранённого на устройстве (нет сети)
        setCachedBasket(c, basket: Basket) {
            c.dispatch('setBasket', basket)
            restoreDraft(c)
            if (isDirty()) {
                unverified = true
                scheduleSync(c)
            }
        },
        createBasket(c) {
            c.commit('setBasket', { token: c.getters.clientProfile.c1code })
            c.dispatch('saveBasket')
        },
        clearBasket(c) {
            c.commit('clearBasketItems')
            c.dispatch('saveBasket')
        },
    },
    getters: {
        basket(state) {
            return state.basket
        },
        basketComment(state) {
            return state.basket.note
        },
        basketSaveError(state) {
            return state.saveError
        },
        basketToken(state) {
            return state.basket.token
        },
        basketProducts(state) {
            return state.basket.items
        },
        basketProductCount(state) {
            return state.basket.item_count
        },
        /*basketFinalTotalPrice(state) {
            return state.basket.total_price_pofaktu
        },*/
        /*basketNetTotalPrice() {
            return state.basket.original_total_price
        },*/
        basketFormattedFinalTotalPrice() {
            return state.basket.total_price
        },
        selectedBasketProducts(state) {
            return state.basket.items.filter(i => i.selected)
        },
        selectedBasketCount(state) {
            return state.basket.items.filter(i => i.selected).length
        },
        allBasketProductsSelected(state) {
            return state.basket.items.length > 0 && state.basket.items.every(i => i.selected)
        },
        someBasketProductsSelected(state) {
            const sel = state.basket.items.filter(i => i.selected).length
            return sel > 0 && sel < state.basket.items.length
        },
        selectedBasketFormattedTotalPrice(state) {
            const total = gaussRound(
                state.basket.items
                    .filter(i => i.selected)
                    .reduce((acc, item) => acc + item.original_line_price, 0),
                2
            )
            return new Intl.NumberFormat('ru', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(total)
        }
    }
}